import { capitalize } from 'lodash';
import type { GTMEvent } from 'root/utils/tracking/GA4/googleTagManager';
import { PlacementEnums, PlacementType } from 'root/utils/tracking/GA4/types';
import { getDecodedValueFromSessionStorage } from 'root/utils/dom/sessionStorage';
import {
    sessionStartTimeKey,
    sessionScrolledKey,
} from 'root/core/tracking/sessionTracking/sessionTracking';

enum EventActionEnums {
    menuAction = 'click_menu',
    submenuAction = 'click_sub_menu',
    secondaryAction = 'click_secondary_menu',
    secondarySubmenuAction = 'click_secondary_sub_menu',
}

enum NavigationCategoryEnums {
    menuCat = 'menu_category',
    submenuCat = 'sub_menu_category',
    labelCat = 'label',
}

enum NavigationGroupEnums {
    main = 'main',
    secondary = 'secondary',
}
enum NavigationLevelEnums {
    parent = 'parent',
    child = 'child',
    grandchild = 'grandchild',
}
enum NavigationPlatformEnums {
    mobile = 'mobile',
    desktop = 'desktop',
}

type EventActionType =
    | EventActionEnums.menuAction
    | EventActionEnums.submenuAction
    | EventActionEnums.secondaryAction
    | EventActionEnums.secondarySubmenuAction;

type NavigationGroupType =
    | NavigationGroupEnums.main
    | NavigationGroupEnums.secondary
    | undefined;
type NavigationLevelType =
    | NavigationLevelEnums.parent
    | NavigationLevelEnums.child
    | NavigationLevelEnums.grandchild
    | undefined;
type NavigationPlatformType =
    | NavigationPlatformEnums.mobile
    | NavigationPlatformEnums.desktop
    | undefined;
interface GTMNavigationEventInterface extends GTMEvent {
    event_action: EventActionType;
    placement: PlacementType;
    menu_category?: string;
    sub_menu_category?: string;
    label?: string;
    session_start_time?: string;
    scrolled_75?: string;
}

export default function navTracking(el: HTMLElement) {
    const navigationLevelEl = el?.closest<HTMLElement>('[data-navigation-level]');
    const navigationPlatformEl = el?.closest<HTMLElement>('[data-navigation-platform]');
    const navigationGroupEl = el?.closest<HTMLElement>('[data-navigation-group]');

    const navigationLevel =
        navigationLevelEl &&
        (navigationLevelEl?.dataset.navigationLevel as NavigationLevelType);

    const navigationPlatform =
        navigationPlatformEl &&
        (navigationPlatformEl?.dataset.navigationPlatform as NavigationPlatformType);

    const navigationGroup =
        navigationGroupEl &&
        (navigationGroupEl?.dataset.navigationGroup as NavigationGroupType);

    const { parent, child, grandchild } = NavigationLevelEnums;
    const { main, secondary } = NavigationGroupEnums;
    const { mobile, desktop } = NavigationPlatformEnums;

    const navigationLevelAssertionMap: Record<string, Record<string, boolean>> = {
        parent: {
            isMainNavigation: navigationLevel === parent && navigationGroup === main,
            isSecondaryNavigation:
                (navigationLevel === parent &&
                    navigationGroup === secondary &&
                    navigationPlatform === desktop) ??
                false,
            isSecondaryNavigationMobile:
                (navigationLevel === parent &&
                    navigationGroup === secondary &&
                    navigationPlatform === mobile) ??
                false,
            isFooterNavigation:
                (el.closest('[data-footer-nav-column]') && navigationLevel === parent) ??
                false,
            isTabsNavigation: navigationLevel === parent ?? false,
        },
        child: {
            isSubNavigation:
                (navigationLevel === child && navigationGroup === main) ?? false,
            isSecondarySubNavigationMobile:
                (navigationLevel === child &&
                    navigationGroup === secondary &&
                    navigationPlatform === mobile) ??
                false,
        },
        grandchild: {
            isSubNavigationLabel:
                (navigationLevel === grandchild && navigationGroup === main) ?? false,

            isSecondaryLabelNavigation:
                (navigationLevel === grandchild &&
                    navigationGroup === secondary &&
                    navigationPlatform === desktop) ??
                false,

            isSecondaryLabelNavigationMobile:
                (navigationLevel === grandchild &&
                    navigationGroup === secondary &&
                    navigationPlatform === mobile) ??
                false,

            isFooterLabelNavigation:
                (el.closest('[data-footer-nav-column]') &&
                    navigationLevel === grandchild) ??
                false,
        },
    };

    const getTabPlacement = (): PlacementType | null => {
        const { nav, hero, footer, body } = PlacementEnums;
        return [nav, hero, footer, body].includes(el?.dataset.placement as PlacementEnums)
            ? (el.dataset.placement as PlacementType)
            : null;
    };
    const getEventAction = (): EventActionType => {
        const { isSecondaryNavigation, isSecondaryNavigationMobile } =
            navigationLevelAssertionMap.parent;
        const { isSubNavigation, isSecondarySubNavigationMobile } =
            navigationLevelAssertionMap.child;
        const {
            isSubNavigationLabel,
            isSecondaryLabelNavigation,
            isSecondaryLabelNavigationMobile,
        } = navigationLevelAssertionMap.grandchild;
        if (isSubNavigation || isSubNavigationLabel) {
            return EventActionEnums.submenuAction;
        } else if (
            isSecondaryNavigation ||
            isSecondaryLabelNavigation ||
            isSecondaryNavigationMobile
        ) {
            return EventActionEnums.secondaryAction;
        } else if (isSecondarySubNavigationMobile || isSecondaryLabelNavigationMobile) {
            return EventActionEnums.secondarySubmenuAction;
        } else {
            return EventActionEnums.menuAction;
        }
    };
    const getEventPlacement = (): PlacementType => {
        const {
            isSecondaryNavigation,
            isSecondaryNavigationMobile,
            isFooterNavigation,
            isTabsNavigation,
        } = navigationLevelAssertionMap.parent;
        const { isSecondarySubNavigationMobile } = navigationLevelAssertionMap.child;
        const {
            isFooterLabelNavigation,
            isSecondaryLabelNavigation,
            isSecondaryLabelNavigationMobile,
        } = navigationLevelAssertionMap.grandchild;
        if (
            isSecondaryNavigation ||
            isSecondaryLabelNavigation ||
            isSecondaryNavigationMobile ||
            isSecondarySubNavigationMobile ||
            isSecondaryLabelNavigationMobile
        ) {
            return PlacementEnums.hero;
        } else if (isFooterNavigation || isFooterLabelNavigation) {
            return PlacementEnums.footer;
        } else if (isTabsNavigation && getTabPlacement()) {
            return getTabPlacement() as PlacementType;
        } else {
            return PlacementEnums.nav;
        }
    };

    const getNavigationCategories = (GTMObject: GTMNavigationEventInterface) => {
        const footerNavEl = el
            .closest('[data-footer-nav-column]')
            ?.querySelector('[data-navigation-level=parent]');

        const footerLabelEl = el.closest('[data-navigation-level=child]');

        const tabEl =
            (el.closest('[role=tab]')?.firstElementChild as HTMLElement | undefined) ||
            el.closest('[role=tab]');

        const navEl =
            (el.closest('[data-navigation-level=parent]')
                ?.firstElementChild as HTMLElement) ||
            el.closest('[data-navigation-level=parent]') ||
            tabEl ||
            footerNavEl;

        const subnavEl =
            (el.closest('[data-navigation-level=child]')
                ?.firstElementChild as HTMLElement) ||
            el.closest('[data-navigation-level=child]');
        const labelEl =
            (el.closest('[data-navigation-level=grandchild]')
                ?.firstElementChild as HTMLElement) ||
            el.closest('[data-navigation-level=grandchild]') ||
            footerLabelEl;

        const getCapitalizedInnerText = (element: HTMLElement) => {
            return capitalize(element?.innerText ?? '');
        };

        /*
         * this is a necessary exception in order to get the root text of the breadcrumb
         * when we have the following breadcrumb structure for the secondary mobile nav
         * root name > last selected item name (Example Retail > Payments)
         * Without this exception, if the user clicked on the "last selected item name" of the breadcrumb
         * the last selected item text will be sent instead of the root parent element text.
         * */
        const getSecondaryNavigationMobileBreadcrumbsRootText = (el: HTMLElement) => {
            const secondaryNavFirstChildMobile = el.closest(
                '.navigation-btf-mobile__menu__list[data-secondary-nav-header]'
            )?.firstElementChild;
            if (
                secondaryNavFirstChildMobile?.classList.contains(
                    'navigation-btf-mobile__item--breadcrumb-first'
                )
            ) {
                return (
                    capitalize(secondaryNavFirstChildMobile?.textContent?.trim()) ?? ''
                );
            }
        };

        const getMenuCategory =
            getSecondaryNavigationMobileBreadcrumbsRootText(navEl) ||
            getCapitalizedInnerText(navEl);
        const getSubMenuCategory = getCapitalizedInnerText(subnavEl);
        const getLabel = getCapitalizedInnerText(labelEl);

        const isNav = Object.values(navigationLevelAssertionMap.parent).includes(true);
        const isSubnav = Object.values(navigationLevelAssertionMap.child).includes(true);
        const isLabel = Object.values(navigationLevelAssertionMap.grandchild).includes(
            true
        );

        const { menuCat, submenuCat, labelCat } = NavigationCategoryEnums;
        if (isNav) {
            getMenuCategory && (GTMObject[menuCat] = getMenuCategory);
        } else if (isSubnav) {
            getMenuCategory && (GTMObject[menuCat] = getMenuCategory);
            getSubMenuCategory && (GTMObject[submenuCat] = getSubMenuCategory);
        } else if (isLabel) {
            getMenuCategory && (GTMObject[menuCat] = getMenuCategory);
            getSubMenuCategory && (GTMObject[submenuCat] = getSubMenuCategory);
            getLabel && (GTMObject[labelCat] = getLabel);
        }

        return GTMObject;
    };

    const getGTMObject = (): GTMNavigationEventInterface => {
        const GTMObject: GTMNavigationEventInterface = {
            event_action: getEventAction(),
            placement: getEventPlacement(),
            scrolled_75: getDecodedValueFromSessionStorage(sessionScrolledKey) ?? 'false',
            session_start_time:
                getDecodedValueFromSessionStorage(sessionStartTimeKey) ?? '0',
        };
        getNavigationCategories(GTMObject);
        return GTMObject;
    };

    if (
        Object.values({
            ...navigationLevelAssertionMap.grandchild,
            ...navigationLevelAssertionMap.child,
            ...navigationLevelAssertionMap.parent,
        }).includes(true)
    ) {
        return getGTMObject();
    }
}
